import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import * as fromQuestionActions from './question.actions';

import { catchError, filter, mergeMap, withLatestFrom } from 'rxjs/operators';
import { QuestionModel } from '../question.model';
import { QuestionService } from '../question.service';
import { Store } from '@ngrx/store';
import { cloneDeep, isArray, isEqual, isNumber, omit } from 'lodash-es';
import { AppState } from '../../store/models/app.state';
import { errorPopup, globalLoading, successPopup } from '../../store/actions/ui.actions';
import { selectCurrentFormStateForm } from '../../form/store/form.state';
import { UtilsService } from '../../core/utils.service';
import { ControlModel } from '../control.model';
import { FormModel } from '../../form/form.model';
import { selectCurrentQuestion, selectQuestionFeatureCurrentQuestion } from './question.state';
import {
  formUpdateQuestion,
  formUpdateQuestions,
  getFormRules,
  populateRuleInList,
  removeRuleFromList,
  removeRulesFromList,
  updateFormIntegrationInList,
  updateFormIntegrationsInList,
} from '../../form/store/form.actions';
import {
  bulkUpdateQuestionIntegration,
  getQuestionRules,
  populateCurrentQuestionRules,
  setQuestionRulesChanged,
} from './question.actions';
import { IFormIntegration } from '../../shared/models/integration/form-integration.interface';

@Injectable()
export class QuestionEffects {
  actions = inject(Actions);
  store = inject(Store<AppState>);
  questionService = inject(QuestionService);
  utilsService: UtilsService = inject(UtilsService);
  loadQuestionListByDataset = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.loadQuestionListByDataset),
      mergeMap(({ datasetId }) =>
        this.questionService.getQuestionList(datasetId).pipe(
          mergeMap((res: QuestionModel[]) => [
            fromQuestionActions.updateQuestionListByDataset({
              questionList: res || [],
            }),
          ]),
          catchError(() => {
            return [];
          }),
        ),
      ),
    );
  });

  loadQuestionListByForm = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.loadQuestionListByForm),
      mergeMap(({ formId }) =>
        this.questionService.getQuestionListByForm(formId).pipe(
          mergeMap((res: QuestionModel[]) => [
            fromQuestionActions.updateQuestionListByForm({
              questionList: res || [],
            }),
          ]),
          catchError(() => {
            return [];
          }),
        ),
      ),
    );
  });

  seachQuestionById = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.searchQuestionById),
      mergeMap(({ id }) =>
        this.questionService.searchById(id).pipe(
          mergeMap((res: QuestionModel) => [
            res
              ? fromQuestionActions.populateCurrentQuestion({ question: res })
              : fromQuestionActions.resetCurrentQuestion(),
          ]),
          catchError(() => {
            return [];
          }),
        ),
      ),
    );
  });

  getQuestionRules = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.getQuestionRules),
      mergeMap(({ questionId }) =>
        this.questionService.getQuestionRules(questionId).pipe(
          mergeMap(rules => {
            const filteredRules = rules
              .filter(r => !r.isDeleted)
              .map(r => {
                const numeric = Number(r.additionalValue);
                return {
                  ...r,
                  additionalValue: r.additionalValue && isNumber(numeric) ? numeric : r.additionalValue,
                };
              });
            return [
              fromQuestionActions.populateCurrentQuestionRules({ rules: filteredRules }),
              fromQuestionActions.populateCurrentQuestionRulesSnapshot({
                rules: cloneDeep(filteredRules),
              }),
              fromQuestionActions.setQuestionRulesChanged({ changed: false }),
            ];
          }),
          catchError(() => {
            return [];
          }),
        ),
      ),
    );
  });
  updateQuestionRule = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.updateQuestionRule),
      withLatestFrom(this.store.select(selectCurrentQuestion)),
      mergeMap(([{ rule }, question]) =>
        this.questionService.updateQuestionRule(rule).pipe(
          mergeMap(() => {
            return [
              successPopup({ message: `QUESTION.MESSAGES.RULES_${rule.isDeleted ? 'DELETED' : 'UPDATED'}` }),
              getQuestionRules({ questionId: question.id }),
              getFormRules({ formId: question.formId }),
            ];
          }),
          catchError(() => {
            return [
              errorPopup({
                error: `QUESTION.MESSAGES.${rule.isDeleted ? 'UNABLE_TO_DELETE_RULES' : 'UNABLE_TO_UPDATE_RULES'}`,
              }),
            ];
          }),
        ),
      ),
    );
  });
  discardQuestionRulesChanges = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.discardQuestionRulesChanges),
      withLatestFrom(this.store.select(selectQuestionFeatureCurrentQuestion)),
      mergeMap(([, question]) => {
        return [
          populateCurrentQuestionRules({ rules: cloneDeep(question.rulesSnapshot) }),
          setQuestionRulesChanged({ changed: false }),
        ];
      }),
      catchError(() => {
        return [];
      }),
    );
  });
  bulkUpdateQuestions = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.bulkUpdateQuestionRules),
      mergeMap(({ rules }) =>
        this.questionService.bulkUpdateQuestionRules(rules).pipe(
          mergeMap(() => {
            return [
              successPopup({ message: 'QUESTION.MESSAGES.RULES_CREATED' }),
              // fromQuestionActions.populateCurrentQuestionRules({ rules }),
              // fromQuestionActions.populateCurrentQuestionRulesSnapshot({
              //   rules: cloneDeep(rules),
              // }),
            ];
          }),
          catchError(() => {
            return [errorPopup({ error: 'QUESTION.MESSAGES.UNABLE_TO_CREATE_RULES' })];
          }),
        ),
      ),
    );
  });
  bulkCreateQuestionRules = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.bulkCreateQuestionRules),
      mergeMap(({ rules }) =>
        this.questionService.bulkCreateQuestionRules(rules).pipe(
          mergeMap(() => {
            return [successPopup({ message: 'QUESTION.MESSAGES.RULE_CREATED' })];
          }),
          catchError(() => {
            return [errorPopup({ error: 'QUESTION.MESSAGES.UNABLE_TO_CREATE_RULE' })];
          }),
        ),
      ),
    );
  });
  bulkCreateUpdateQuestionRules = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.bulkCreateUpdateQuestionRules),
      withLatestFrom(this.store.select(selectCurrentQuestion), this.store.select(selectCurrentFormStateForm)),
      mergeMap(([{ update, create }, question, form]) =>
        this.questionService.bulkCreateUpdateQuestionRules({ update, create }).pipe(
          mergeMap(() => {
            return [
              successPopup({ message: 'QUESTION.MESSAGES.RULES_CREATED_UPDATED' }),
              getQuestionRules({ questionId: question.id }),
              getFormRules({ formId: question.formId }),
              bulkUpdateQuestionIntegration({ questions: [question], form }),
            ];
          }),
          catchError(() => {
            return [errorPopup({ error: 'QUESTION.MESSAGES.UNABLE_TO_CREATE_RULES_UPDATED' })];
          }),
        ),
      ),
    );
  });
  populateCurrentQuestionRules = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.populateCurrentQuestion),
      filter(({ question }) => !!question.id),
      mergeMap(({ question }) => {
        return [fromQuestionActions.getQuestionRules({ questionId: question.id })];
      }),
      catchError(() => {
        return [];
      }),
    );
  });

  createQuestion = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.createQuestion),
      withLatestFrom(this.store.select(selectCurrentFormStateForm)),
      mergeMap(([{ question }, form]) =>
        this.questionService.create(question, form).pipe(
          mergeMap((res: QuestionModel) => {
            return [
              fromQuestionActions.pendingCreateUpdateQuestion({ pending: false }),
              fromQuestionActions.successCreateUpdateQuestion({
                success: true,
                response: res,
                isCreate: true,
              }),
              globalLoading(false),
              fromQuestionActions.updateQuestionInList({ question: res }),
              res.formId
                ? fromQuestionActions.addQuestionInListForm({ question: res })
                : fromQuestionActions.addQuestionInListDataset({ question: res }),
              ...(question.controls.length
                ? [
                    fromQuestionActions.updateQuestionIntegration({
                      form,
                      question: res,
                    }),
                  ]
                : []),
            ];
          }),
          catchError(() => {
            this.questionService.setQuestionAutoCreateMode(false);
            return [globalLoading(false)];
          }),
        ),
      ),
    );
  });

  updateQuestion = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.updateQuestion),
      concatLatestFrom(() => this.store.select(selectCurrentFormStateForm)),

      mergeMap(([{ question, skipIntegration, successMessage }, form]) => {
        //return this.questionService.update(this.updateQuestionAndControlUUID(question, form) as QuestionModel).pipe(

        return this.questionService.update(question, form).pipe(
          mergeMap((res: QuestionModel) => {
            return [
              globalLoading(false),
              fromQuestionActions.updateQuestionInList({ question: res }),
              formUpdateQuestion({ question: res }),
              fromQuestionActions.pendingCreateUpdateQuestion({ pending: false }),
              fromQuestionActions.successCreateUpdateQuestion({ success: true, isCreate: false, successMessage }),
              fromQuestionActions.populateCurrentQuestion({ question: res }),
              ...(skipIntegration
                ? []
                : [
                    fromQuestionActions.updateQuestionIntegration({
                      form,
                      question: res,
                    }),
                  ]),
            ];
          }),
          catchError(() => {
            return [globalLoading(false)];
          }),
        );
      }),
    );
  });

  updateQuestions = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.updateQuestions),
      concatLatestFrom(() => this.store.select(selectCurrentFormStateForm)),
      mergeMap(([{ questions }, form]) =>
        //this.questionService.updateMultiple(this.updateQuestionAndControlUUID(questions, form) as QuestionModel[]).pipe(
        this.questionService.updateMultiple(questions).pipe(
          mergeMap((res: QuestionModel[]) => {
            return [
              globalLoading(false),
              fromQuestionActions.updateQuestionsInList({ questions: res }),
              formUpdateQuestions({ questions: res }),
              fromQuestionActions.bulkUpdateQuestionIntegration({ form, questions: res }),
            ];
          }),
          catchError(() => {
            return [globalLoading(false)];
          }),
        ),
      ),
    );
  });

  deleteQuestion = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.deleteQuestion),
      mergeMap(({ id, isFormQuestion }) =>
        this.questionService.deleteQuestion(id, isFormQuestion).pipe(
          mergeMap(() => [
            fromQuestionActions.removeQuestion({ id }),
            successPopup({ message: 'QUESTION.MESSAGES.QUESTION_DELETED' }),
          ]),
          catchError(() => {
            return [errorPopup({ error: 'QUESTION.MESSAGES.UNABLE_TO_DELETE_QUESTION' })];
          }),
        ),
      ),
    );
  });

  populateCurrentQuestion = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.populateCurrentQuestion),
      filter(({ skipPopulateSnapshot }) => !skipPopulateSnapshot),
      mergeMap(({ question }) => {
        return [
          fromQuestionActions.populateCurrentQuestionSnapshot({
            question,
          }),
        ];
      }),
      catchError(() => []),
    );
  });

  updateRulesChanged = createEffect(() => {
    return this.actions.pipe(
      ofType(
        fromQuestionActions.updateQuestionRuleInList,
        fromQuestionActions.populateQuestionRule,
        fromQuestionActions.removeQuestionRuleByOrder,
        fromQuestionActions.deleteQuestionRule,
      ),
      withLatestFrom(this.store.select(selectQuestionFeatureCurrentQuestion)),
      mergeMap(([, { rules, rulesSnapshot }]) => {
        const rulesAreEqual = this.questionService.areRulesEqual(rules, rulesSnapshot);
        return [
          fromQuestionActions.setQuestionRulesChanged({
            changed: !rulesAreEqual,
          }),
        ];
      }),
      catchError(() => []),
    );
  });

  updatePendingChanges = createEffect(() => {
    return this.actions.pipe(
      ofType(
        fromQuestionActions.populateCurrentQuestionSnapshot,
        fromQuestionActions.successCreateUpdateQuestion,
        fromQuestionActions.updateQuestionField,
        fromQuestionActions.updateQuestionFields,
        fromQuestionActions.updateQuestion,
      ),
      withLatestFrom(this.store.select(selectQuestionFeatureCurrentQuestion)),
      mergeMap(([, { question, questionSnapshot }]) => {
        const current: QuestionModel = cloneDeep(question);
        const snapshot: QuestionModel = cloneDeep(questionSnapshot);

        current.controls = this.questionService.removeDropZonesDeep(current.controls);
        snapshot.controls = this.questionService.removeDropZonesDeep(snapshot.controls);
        const controlsAreEqual = this.areControlsEqual(current.controls, snapshot.controls);

        const formDataEqual = isEqual(omit(current, 'controls'), omit(snapshot, 'controls'));
        return [
          fromQuestionActions.checkCurrentQuestionForPendingChanges({
            hasChanges: !formDataEqual || !controlsAreEqual,
          }),
        ];
      }),
      catchError(() => []),
    );
  });

  successCreateUpdateQuestion = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.successCreateUpdateQuestion),
      mergeMap(({ isCreate, successMessage }) => {
        if (isCreate) {
          return [];
        }
        return [successPopup({ message: successMessage ?? 'QUESTION.MESSAGES.QUESTION_UPDATED' })];
      }),
      catchError(() => [errorPopup({ error: 'QUESTION.MESSAGES.UNABLE_TO_UPDATE_QUESTION' })]),
    );
  });

  deleteMultipleQuestions = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.deleteMultipleQuestions),
      mergeMap(data => {
        return this.questionService.deleteQuestions(data.ids).pipe(
          mergeMap(() => [fromQuestionActions.removeQuestion({ id: data.ids })]),
          catchError(() => []),
        );
      }),
    );
  });

  softDeleteMultipleQuestions = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.softDeleteMultipleQuestions),
      withLatestFrom(this.store.select(selectCurrentFormStateForm)),
      mergeMap(([data, form]) => {
        const questions: QuestionModel[] = data.ids
          .map(id => {
            return form.questionsPopulated[id];
          })
          .filter(q => !!q);
        const questionsToDelete = questions.map(question => {
          const updatedQuestion: QuestionModel = {
            ...question,
            isDeleted: true,
            controls: question.controls?.map(control => ({ ...control, isDeleted: true })),
          };
          //@TODO CHECK HERE ALSO

          // updatedQuestion = this.integrationService.transformQuestion(updatedQuestion, form);
          return updatedQuestion;
        });
        return this.questionService.updateMultiple(questionsToDelete).pipe(
          mergeMap(() => [bulkUpdateQuestionIntegration({ form, questions: questionsToDelete })]),
          catchError(() => []),
        );
      }),
    );
  });

  updateQuestionIntegration = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.updateQuestionIntegration),
      mergeMap(({ form, question }) => {
        return this.questionService.updateQuestionIntegration(form, question).pipe(
          mergeMap((integration: IFormIntegration) => {
            return [updateFormIntegrationInList({ integration })];
          }),
          catchError(e => {
            const error = this.utilsService.extractErrorMessage(
              e.error?.error || '',
              'QUESTION.MESSAGES.UNABLE_TO_UPDATE_QUESTION_INTEGRATION',
            );
            return [errorPopup({ error }), globalLoading(false)];
          }),
        );
      }),
    );
  });
  bulkUpdateQuestionIntegration = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.bulkUpdateQuestionIntegration),
      mergeMap(({ form, questions }) => {
        return this.questionService.bulkUpdateQuestionIntegration(form, questions).pipe(
          mergeMap((integrations: IFormIntegration[]) => [updateFormIntegrationsInList({ integrations })]),
          catchError(() => {
            return [];
          }),
        );
      }),
    );
  });

  createQuestionRule = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.createQuestionRule),
      mergeMap(({ rule }) => {
        return this.questionService.createQuestionRule(rule).pipe(
          mergeMap(() => [fromQuestionActions.populateQuestionRule({ rule }), populateRuleInList({ rule })]),
          catchError(() => {
            return [];
          }),
        );
      }),
    );
  });
  deleteQuestionRule = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.deleteQuestionRule),
      withLatestFrom(this.store.select(selectCurrentQuestion)),
      mergeMap(([{ ruleId }, question]) => {
        return this.questionService.deleteQuestionRule(ruleId).pipe(
          mergeMap(() => [
            fromQuestionActions.getQuestionRules({ questionId: question.id }),
            removeRuleFromList({ ruleId }),
            successPopup({ message: 'QUESTION.MESSAGES.RULE_DELETED' }),
          ]),
          catchError(() => {
            return [errorPopup({ error: 'QUESTION.MESSAGES.UNABLE_TO_DELETE_RULE' })];
          }),
        );
      }),
    );
  });

  bulkDeleteQuestionRules = createEffect(() => {
    return this.actions.pipe(
      ofType(fromQuestionActions.bulkDeleteQuestionRules),
      withLatestFrom(this.store.select(selectCurrentQuestion)),
      mergeMap(([{ ruleIds }, question]) => {
        return this.questionService.bulkDeleteQuestionRules(ruleIds).pipe(
          mergeMap(() => [
            fromQuestionActions.getQuestionRules({ questionId: question.id }),
            removeRulesFromList({ ruleIds }),
            successPopup({ message: 'QUESTION.MESSAGES.RULES_DELETED' }),
          ]),
          catchError(() => {
            return [errorPopup({ error: 'QUESTION.MESSAGES.UNABLE_TO_DELETE_RULES' })];
          }),
        );
      }),
    );
  });

  private updateQuestionAndControlUUID(
    question: QuestionModel | QuestionModel[],
    form: FormModel,
  ): QuestionModel | QuestionModel[] {
    if (!form.isAmended) {
      return question;
    }

    // if the form is amended we need to update the question guid and all corresponding controls with the new guid

    if (isArray(question)) {
      return question.map((q: QuestionModel) => this.updateQuestionAndControlUUIDFn(q));
    }
    return this.updateQuestionAndControlUUIDFn(question as QuestionModel);
  }

  private updateQuestionAndControlUUIDFn(question: QuestionModel): QuestionModel {
    const clonedQuestion = cloneDeep(question);
    const newUUID = this.utilsService.generateUUID();
    clonedQuestion.questionGuid = newUUID;
    if (clonedQuestion.controls.length) {
      clonedQuestion.controls = clonedQuestion.controls.map((control: ControlModel) => ({
        ...control,
        question_uuid: newUUID,
      }));
    }
    return clonedQuestion;
  }

  private areControlsEqual(currenControls: ControlModel[], snapshotControls: ControlModel[]): boolean {
    if (snapshotControls.length !== currenControls.length) {
      return false;
    }
    for (const control of snapshotControls) {
      const toFind = currenControls.find(c => c.controlID === control.controlID);
      if (!toFind) {
        return false;
      }
      if (toFind && !isEqual(control, toFind)) {
        return false;
      }
    }

    return true;
  }
}
